import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import * as Papa from "papaparse";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import {
  X,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Eye,
  Download,
} from "lucide-react";
import { useToast } from "../ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { csvLeadService } from "../../services/csvLeadService";

interface CsvUploaderProps {
  organizationId: string;
  onUploadComplete?: () => void;
  campaignId?: string;
  callflowId?: string;
}

interface CsvRow {
  [key: string]: string;
}

interface ColumnMapping {
  csvColumn: string;
  leadField: string;
}

interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: Array<{ row: number; error: string }>;
}

const LEAD_FIELDS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "country_code", label: "Country Code" },
  { value: "phone_number", label: "Phone Number" },
  { value: "skip", label: "Skip this column" },
];

export function CsvUploader({
  organizationId,
  onUploadComplete,
  campaignId,
  callflowId,
}: CsvUploaderProps) {
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setFileName(file.name);
    setImportResult(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.log("CSV parsed:", results);
        const data = results.data as CsvRow[];
        const headers = results.meta.fields || [];

        setCsvData(data);
        setCsvHeaders(headers);

        // Auto-map columns based on common names
        const autoMapping = headers.map((header) => {
          const lowerHeader = header.toLowerCase().trim();
          let mappedField = "skip";

          if (
            lowerHeader.includes("first") &&
            lowerHeader.includes("name")
          ) {
            mappedField = "first_name";
          } else if (
            lowerHeader.includes("last") &&
            lowerHeader.includes("name")
          ) {
            mappedField = "last_name";
          } else if (lowerHeader.includes("email")) {
            mappedField = "email";
          } else if (
            lowerHeader.includes("country") &&
            (lowerHeader.includes("code") || lowerHeader.includes("prefix"))
          ) {
            mappedField = "country_code";
          } else if (
            lowerHeader.includes("phone") ||
            lowerHeader.includes("mobile") ||
            lowerHeader.includes("number")
          ) {
            mappedField = "phone_number";
          }

          return {
            csvColumn: header,
            leadField: mappedField,
          };
        });

        setColumnMapping(autoMapping);
        toast({
          title: "CSV Loaded",
          description: `Found ${data.length} rows with ${headers.length} columns`,
        });
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        toast({
          title: "Error parsing CSV",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
  });

  const handleColumnMappingChange = (csvColumn: string, leadField: string) => {
    setColumnMapping((prev) =>
      prev.map((mapping) =>
        mapping.csvColumn === csvColumn
          ? { ...mapping, leadField }
          : mapping
      )
    );
  };

  const handleImport = async () => {
    if (!organizationId) {
      toast({
        title: "Error",
        description: "Organization ID is required",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      // Transform CSV data to lead data based on column mapping
      const leadsToImport = csvData.map((row) => {
        const lead: any = {};
        columnMapping.forEach((mapping) => {
          if (mapping.leadField !== "skip") {
            lead[mapping.leadField] = row[mapping.csvColumn] || "";
          }
        });
        return lead;
      });

      console.log("Importing leads:", leadsToImport);

      console.log('=== CSV UPLOADER DEBUG ===');
      console.log('organizationId:', organizationId);
      console.log('campaignId:', campaignId);
      console.log('callflowId:', callflowId);
      console.log('leadsToImport count:', leadsToImport.length);

      // Import leads in batches
      const result = await csvLeadService.bulkCreateLeads(
        leadsToImport,
        organizationId,
        campaignId,
        (current, total) => {
          setProgress(Math.round((current / total) * 100));
        },
        fileName, // Pass the CSV filename
        callflowId // Pass the callflow ID
      );

      setImportResult(result);
      setProgress(100);

      if (result.successful > 0) {
        toast({
          title: "Import Complete",
          description: `Successfully imported ${result.successful} out of ${result.total} leads`,
        });

        if (onUploadComplete) {
          onUploadComplete();
        }
      }

      if (result.failed > 0) {
        toast({
          title: "Some imports failed",
          description: `${result.failed} leads failed to import. Check the error details below.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    setCsvData([]);
    setCsvHeaders([]);
    setColumnMapping([]);
    setFileName("");
    setProgress(0);
    setImportResult(null);
  };

  // Check which required fields are missing
  const getMissingRequiredFields = () => {
    if (csvData.length === 0) return [];

    const requiredFields = ["first_name", "last_name", "country_code", "phone_number"];
    const mappedFields = columnMapping.map((m) => m.leadField);

    return requiredFields.filter((field) => !mappedFields.includes(field));
  };

  const missingFields = getMissingRequiredFields();

  const canImport =
    csvData.length > 0 &&
    columnMapping.some((m) => m.leadField === "first_name") &&
    columnMapping.some((m) => m.leadField === "last_name") &&
    columnMapping.some((m) => m.leadField === "country_code") &&
    columnMapping.some((m) => m.leadField === "phone_number") &&
    !importResult; // Disable after import is complete

  return (
    <div className="space-y-4">
      {csvData.length === 0 ? (
        <>
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => csvLeadService.downloadTemplate()}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? "border-primary bg-primary/10"
                : "border-gray-300 hover:border-primary/50"
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center space-y-2">
              <Upload className="h-10 w-10 text-gray-400" />
              <p className="text-lg font-medium">
                {isDragActive
                  ? "Drop the CSV file here..."
                  : "Drag & drop CSV file here, or click to select"}
              </p>
              <p className="text-sm text-gray-500">
                CSV files up to 10MB. Required: first_name, last_name, country_code, phone_number
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {/* File Info */}
          <div className="flex items-center justify-between border rounded-md p-3 bg-muted/50">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {csvData.length} rows, {csvHeaders.length} columns
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={isProcessing}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Column Mapping */}
          <div className="border rounded-md p-4">
            <h4 className="text-sm font-medium mb-3">Map CSV Columns to Lead Fields</h4>
            <div className="space-y-2">
              {columnMapping.map((mapping) => (
                <div
                  key={mapping.csvColumn}
                  className="flex items-center space-x-3"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      {mapping.csvColumn}
                    </p>
                  </div>
                  <div className="flex-1">
                    <Select
                      value={mapping.leadField}
                      onValueChange={(value) =>
                        handleColumnMappingChange(mapping.csvColumn, value)
                      }
                      disabled={isProcessing}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_FIELDS.map((field) => (
                          <SelectItem key={field.value} value={field.value}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            {/* Missing Required Fields Warning */}
            {missingFields.length > 0 && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">
                      Missing Required Fields
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      Please map the following required columns:{" "}
                      <span className="font-semibold">
                        {missingFields.map((field) => field.replace("_", " ")).join(", ")}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="border rounded-md p-4">
            <h4 className="text-sm font-medium mb-3">
              Preview (first 5 rows)
            </h4>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {csvHeaders.map((header) => (
                      <TableHead key={header}>{header}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.slice(0, 5).map((row, idx) => (
                    <TableRow key={idx}>
                      {csvHeaders.map((header) => (
                        <TableCell key={header} className="text-sm">
                          {row[header]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Import Progress */}
          {isProcessing && (
            <div className="border rounded-md p-4">
              <p className="text-sm font-medium mb-2">Importing leads...</p>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
            </div>
          )}

          {/* Import Results */}
          {importResult && (
            <div className="border rounded-md p-4 bg-muted/50">
              <h4 className="text-sm font-medium mb-3">Import Results</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">
                    {importResult.successful} leads imported successfully
                  </span>
                </div>
                {importResult.failed > 0 && (
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-sm text-red-600">
                        {importResult.failed} leads failed to import
                      </span>
                      {importResult.errors.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {importResult.errors.slice(0, 5).map((error, idx) => (
                            <p key={idx} className="text-xs text-gray-600">
                              Row {error.row}: {error.error}
                            </p>
                          ))}
                          {importResult.errors.length > 5 && (
                            <p className="text-xs text-gray-500">
                              ...and {importResult.errors.length - 5} more errors
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Button */}
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={isProcessing}
            >
              {importResult ? "Upload Another CSV" : "Cancel"}
            </Button>
            <Button
              onClick={handleImport}
              disabled={!canImport || isProcessing}
            >
              {isProcessing
                ? "Importing..."
                : importResult
                ? "Import Complete"
                : `Import ${csvData.length} Leads`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
